<template>
  <div>
    <div class="header">
      <h3 class="mb-4 text-lg font-semibold dark:text-white">Bank Account Information</h3>
      <p class="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Add or update your bank account details for receiving payments
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-8">
      <div class="w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
    </div>

    <!-- Bank Account Form -->
    <div v-else class="py-6">

      <div v-if="bankAccount" class="gap-4 text-gray-700 dark:text-gray-200 md:grid-cols-2">
        <div>Bank Name: {{ bankAccount.bank_name }}</div>
        <div>Account Number: {{ bankAccount.account_number }}</div>
        <div>Account Name: {{ bankAccount.account_name }}</div>
        <button v-if="!showBankAccountForm" @click="showBankAccountForm = true"
          class="px-4 py-2 mt-6 text-white bg-indigo-600 rounded hover:bg-indigo-700">
          Update Bank Information
        </button>
      </div>
      <div class="text-gray-700 dark:text-gray-200" v-else>No Bank Info Found</div>
      <div class="transition-all duration-300 ease-linear sm:mt-6"
        :class="showBankAccountForm ? 'h-[700px] opacity-100' : 'h-0 opacity-0'">
        <!-- Form Header -->
        <div class="py-4 sm:border-b dark:border-gray-700">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ bankAccount ? 'Replace Bank Account' : 'Add Bank Account' }}
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ 'Enter your bank account details for receiving payments' }}
            </p>
          </div>
        </div>


        <!-- Form Content -->
        <form @submit.prevent="submitForm" class="mt-6 space-y-6">
          <!-- Basic Information -->
          <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <SearchableDropdown
                label="Bank Name *"
                input-id="bank_name"
                required
                placeholder="Search for a bank..."
                help-text="Search and select your bank from the list"
                item-label="banks"
                :items="banks"
                :loading="loadingBanks"
                display-key="name"
                :search-keys="['name', 'code']"
                @item-selected="handleBankSelected" />
              <p v-if="validationErrors.bank_code" class="mt-1 text-sm text-red-600">
                {{ validationErrors.bank_code }}
              </p>
            </div>

            <div>
              <label for="account_number" class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Account Number *
              </label>
              <input
                id="account_number"
                v-model="account_number"
                type="text"
                required
                class="px-3 py-2 w-full rounded-md border border-gray-300 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 1234567890" />
              <p v-if="validationErrors.account_number" class="mt-1 text-sm text-red-600">
                {{ validationErrors.account_number }}
              </p>
            </div>

            <div>
              <label for="account_name" class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Account Holder Name *
              </label>
              <input
                id="account_name"
                v-model="account_name"
                disabled
                type="text"
                required
                class="px-3 py-2 w-full rounded-md border border-gray-300 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., John Doe" />
              <p v-if="validationErrors.account_name" class="mt-1 text-sm text-red-600">
                {{ validationErrors.account_name }}
              </p>
            </div>
            <div class="flex items-center space-x-2 dark:text-white" v-if="isFetchingBankAccount">
              <div>Fetching bank account...</div>
              <div class="w-5 h-5 rounded-full border-b-2 border-blue-600 animate-spin"></div>
            </div>

            <!-- Hidden bank_code field -->
            <input
              v-model="bank_code"
              type="hidden"
              required />
          </div>

          <!-- Form Actions -->
          <div
            class="justify-between pt-6 space-y-4 border-t sm:space-y-0 sm:items-center sm:flex dark:border-gray-200">

            <div class="text-sm text-gray-600 dark:text-gray-200">
              <p>Your bank account information will be securely stored and used for payments.</p>
            </div>

            <primary-button
              type="submit"
              :disabled="submitting || !account_number || !bank_code || !account_name"
              class="px-6 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors sm:float-none float-end hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {{ submitting ? 'Saving...' : (bankAccount ? 'Update Account' : 'Save Account') }}
            </primary-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import api from '@/services/axios'
import { useAuthStore } from '@/stores/authStore'
import { storeToRefs } from 'pinia'
import SearchableDropdown from '@/components/general/SearchableDropdown.vue'
import { useFormValidation } from '@/composables/useFormValidation'
import { bankAccountSchema } from '@/formValidationschemas'
import { useField } from 'vee-validate';
import { useDebounceFn } from '@vueuse/core'
import PrimaryButton from '@/components/general/PrimaryButton.vue'
import { clearCacheAndReload } from '@/utils'

interface BankAccount {
  id: number
  user_id: number
  bank_name: string
  account_number: string
  account_name: string
  bank_code: string
  transaction_receipt_code: string
}


interface Bank {
  id: number
  name: string
  code: string
  country: string
}



const { handleSubmit, errors: validationErrors, resetForm, setErrors } = useFormValidation(bankAccountSchema)
const bankAccount = ref<BankAccount | null>(null)
const loading = ref(true)
const submitting = ref(false)
const { value: bank_name } = useField('bank_name')
const { value: account_number } = useField('account_number')
const { value: account_name } = useField('account_name')
const { value: bank_code } = useField('bank_code')
// Bank search functionality
const banks = ref<Bank[]>([])
const loadingBanks = ref(false)
const toast = useToast()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore);

const showBankAccountForm = ref(false)
// Determine API endpoint based on user role
const apiEndpoint = computed(() => {
  const role = user.value?.role
  return role ? `/api/${role === 'delivery_driver' ? 'driver' : role}/bank-account` : '/api/bank-account'
})

const isFetchingBankAccount = ref(false)

const debouncedValidateBankAccount = useDebounceFn(() => {
  account_name.value = ''
  validateBankAccount()
}, 500)

watch(account_number, debouncedValidateBankAccount)
watch(bank_code, () => {
  validateBankAccount()
})

// Fetch bank account
const fetchBankAccount = async () => {
  const role = user.value?.role
  try {
    loading.value = true
    let endpoint = ''
    if (role === 'business' || role === 'delivery_driver') {
      endpoint = `/api/${role === 'delivery_driver' ? 'driver' : role}/bank-account`
    } else {
      endpoint = '/api/bank-account'
    }
    const response = await api.get(endpoint)
    if (response.data.success) {
      bankAccount.value = response.data.data
    }else {
      showBankAccountForm.value = true;
    }
  } catch (error: unknown) {
    const errorResponse = error as { response?: { status?: number } }
    if (errorResponse.response?.status === 404) {
      showBankAccountForm.value = true;
      // console.error('Error fetching bank account:', error)
      // toast.add({
      //   severity: 'error',
      //   summary: 'Error',
      //   detail: 'Failed to load bank account information',
      //   life: 3000
      // })
    }
  } finally {
    loading.value = false
  }
}

// Fetch banks from Paystack
const fetchBanks = async () => {
  try {
    loadingBanks.value = true
    const response = await api.get('/api/banks')
    if (response.data.success) {
      banks.value = response.data.data
    }
  } catch (error: unknown) {
    console.error('Error fetching banks:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load banks',
      life: 3000
    })
  } finally {
    loadingBanks.value = false
  }
}

// Handle bank selection from BankSearch component
const handleBankSelected = (bank: Bank) => {
  bank_name.value = bank.name
  bank_code.value = bank.code
}

const validateBankAccount = handleSubmit(async (values) => {
  if (!values.bank_code || values.account_number.length !== 10) {
    return;
  }
  account_name.value = ''
  isFetchingBankAccount.value = true
  try {
    const response = await api.get(`/api/bank-account/validate?account_number=${values.account_number}&bank_code=${values.bank_code}`)
    if (response.data.status) {
      account_name.value = response.data.data.account_name
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response.data.message,
        life: 3000
      })
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: response.data.message,
        life: 3000
      })
    }
  } catch (error: unknown) {
    const errorResponse = error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorResponse.response?.data?.message || 'An error occurred while saving the bank account.',
      life: 3000
    })
  } finally {
    isFetchingBankAccount.value = false
  }
})


// Submit form
const submitForm = handleSubmit(async (values) => {
  try {
    submitting.value = true
    const endpoint = apiEndpoint.value

    const response = await api.post(endpoint, {
      bank_name: (values.bank_name || bank_name.value),
      account_number: (values.account_number || account_number.value).trim(),
      account_name: values.account_name || account_name.value,
      bank_code: values.bank_code || bank_code.value,
      transaction_receipt_code: bankAccount.value?.transaction_receipt_code || '',
    })

    if (response.data.success) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response.data.message,
        life: 3000
      })
      resetForm();
      showBankAccountForm.value = false;
      await fetchBankAccount();
      clearCacheAndReload();

      if (bankAccount.value) {
        // Update local state
        Object.assign(bankAccount.value!, response.data.data)
      } else {
        // Set the new bank account
        bankAccount.value = response.data.data
      }

      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response.data.message,
        life: 3000
      })
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: response.data.message,
        life: 3000
      })
    }
  } catch (error: unknown) {
    const errorResponse = error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorResponse.response?.data?.message || 'An error occurred while saving the bank account.',
      life: 3000
    })

  } finally {
    submitting.value = false
  }
})



// Refresh bank account data
const refresh = () => {
  fetchBankAccount()
}

// Expose refresh method for parent components
defineExpose({ refresh })

onMounted(() => {
  fetchBankAccount()
  fetchBanks()
})
</script>
